import { combineReducers } from "redux";
import tokenStorage from "./store/middlewares/tokenStorage";
import headers from "./store/middlewares/headers";
import api from "./store/middlewares/api";
import toastMiddleware from "./store/middlewares/toastMiddleware";

import authReducer from "./store/reducers/authReducer";
import dashboardReducer from "./store/reducers/dashboardReducer";
import userManagementReducer from "./store/reducers/userManagement";
import layoutReducer from "./store/reducers/layoutReducer";
import themeConfigurationReducer from "./store/reducers/themeConfiguration";
import messageReducer from "./store/reducers/messageReducer";
import dataSettingsReducer from "./store/reducers/dataSettings/dataSettingsReducer";
import fileManagementReducer from "./store/reducers/fileManagement";
import siteConfigurationReducer from "./store/reducers/siteConfiguration";
import pageManagement from "./store/reducers/dashboardSetup/pageManagement";
import userNotifications from "./store/reducers/userNotifications";
import connectionsReducer from "./store/reducers/dataSettings/connectionsReducer";
import chartsReducer from "./store/reducers/dashboardSetup/chartsReducer";
import etlConfigReducer from "./store/reducers/dataSettings/etlConfigReducer";
import queryExecSettingsReducer from "./store/reducers/queryExecSettings";
import dataSetupReducer from "./store/reducers/dataSetup/dataSetupReducer";
import monitoringReducer from "./store/reducers/monitoringReducer";
import dataDictionaryReducer from "./store/reducers/dataDictionaryReducer";
import metricsReducer from "./store/reducers/metricsReducer";
import {
  shallowEqual,
  TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from "react-redux";
import headerKpisReducer from "./store/reducers/headerKpisReducer";
import geoMapReducer from "./store/reducers/geoMapReducer";
import activeTableReducer from "./store/reducers/activeTableReducer";
import automationReducer from "./store/reducers/automationReducer";
import {
  EXECUTE_QUERY_SUCCESS,
  GET_VISUALIZATION_PREVIEW_START,
  GET_VISUALIZATION_PREVIEW_SUCCESS,
  LOAD_HEADER_CONFIG_SUCCESS,
  SET_ACTIVE_TAB,
  SET_CHART_VISIBILITY,
  SET_LOAD_BUNDLE,
} from "./store/actions/actionTypes";
import { produce } from "immer";
import commentsReducer from "./store/reducers/dashboardSetup/commentsReducer";
import htmlTemplatesReducer from "./store/reducers/htmlTemplatesReducer";
import usageReducer from "./store/reducers/usageReducer";
import { configureStore } from "@reduxjs/toolkit";

export const rootReducer = combineReducers({
  auth: authReducer,
  layout: layoutReducer,
  dashboard: dashboardReducer,
  themeConfiguration: themeConfigurationReducer,
  userManagement: userManagementReducer,
  dataSettings: dataSettingsReducer,
  fileManagement: fileManagementReducer,
  siteConfiguration: siteConfigurationReducer,
  message: messageReducer,
  userNotifications: userNotifications,
  pageManagement: pageManagement,
  connections: connectionsReducer,
  charts: chartsReducer,
  queryExecSettings: queryExecSettingsReducer,
  etlConfigs: etlConfigReducer,
  dataSetup: dataSetupReducer,
  monitoring: monitoringReducer,
  dataDictionary: dataDictionaryReducer,
  headerKpis: headerKpisReducer,
  geoMap: geoMapReducer,
  automation: automationReducer,
  activeTable: activeTableReducer,
  comments: commentsReducer,
  htmlTemplates: htmlTemplatesReducer,
  usage: usageReducer,
  metrics: metricsReducer,
});

/**
 * For when returning an object in the selector callback to save on rerenders.
 */
export function useShallowEqualSelector(selector) {
  return useSelector(selector, shallowEqual);
}

// Create store with configureStore
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    }).concat([tokenStorage, headers, api, toastMiddleware]),
  devTools: {
    name: "InsightOut Frontend",
    actionSanitizer,
    stateSanitizer,
  },
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Expose store when run in Cypress
if (window.Cypress) {
  window.store = store;
}

const sanitizedText = "(sanitized)";

// Reduces memory usage in Redux Devtools.
function actionSanitizer(action) {
  const newAction = produce(action, (draft) => {
    if (action.type === SET_ACTIVE_TAB) {
      draft.data = sanitizedText;
      return draft;
    }
    if (action.type === SET_CHART_VISIBILITY) {
      draft.filters = sanitizedText;
      return draft;
    }
    if (action.type === SET_LOAD_BUNDLE) {
      draft.charts = sanitizedText;
      return draft;
    }
    if (String(action.type).endsWith("_SUCCESS")) {
      draft.results = sanitizedText;
    }
    if (action.type === LOAD_HEADER_CONFIG_SUCCESS) {
      draft.responseData = sanitizedText;
    }
    if (action.type === EXECUTE_QUERY_SUCCESS && draft.meta.api) {
      draft.meta.api.payload = sanitizedText;
    }
    if (action.type === GET_VISUALIZATION_PREVIEW_START) {
      draft.config = sanitizedText;
    }
    if (action.type === GET_VISUALIZATION_PREVIEW_SUCCESS) {
      draft.chart = sanitizedText;
      draft.result = sanitizedText;
      draft.cacheKey = sanitizedText;
    }

    return draft;
  });
  if (window.actionSanitizerCallback) {
    // Option to override sanitizer result. For use in production deploys.
    const callbackResultAction = window.actionSanitizerCallback(
      action,
      newAction
    );

    if (callbackResultAction) {
      return callbackResultAction;
    }
  }
  return newAction;
}

// Reduces memory usage in Redux Devtools.
function stateSanitizer(state) {
  const newState = produce(state, (draft) => {
    if (state.layout?.tabs) {
      draft.layout.tabs = sanitizedText;
    }
    if (state.layout?.menuFilters) {
      draft.layout.menuFilters = sanitizedText;
    }
    if (state.layout?.activeTab?.blocks) {
      draft.layout.activeTab.blocks = sanitizedText;
    }
    if (state.layout?.header?.blocks) {
      draft.layout.header.blocks = sanitizedText;
    }
    if (state.dashboard?.blocks) {
      draft.dashboard.blocks = sanitizedText;
    }
    if (state.pageManagement?.pages) {
      draft.pageManagement.pages = sanitizedText;
    }
    if (state.fileManagement?.etlConfigs) {
      draft.fileManagement.etlConfigs = sanitizedText;
    }
    if (state.etlConfigs?.etlConfigs) {
      draft.etlConfigs.etlConfigs = sanitizedText;
    }
    if (state.dataSettings?.queryables) {
      draft.dataSettings.queryables = sanitizedText;
    }
    if (state.dataSettings?.queries) {
      draft.dataSettings.queries = sanitizedText;
    }
    if (state.charts?.visualizations) {
      draft.charts.visualizations = sanitizedText;
    }
  });
  if (window.stateSanitizerCallback) {
    // Option to override sanitizer result. For use in production deploys.
    const callbackResultState = window.stateSanitizerCallback(state, newState);

    if (callbackResultState) {
      return callbackResultState;
    }
  }

  return newState;
}
